import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, RefreshCwIcon, CreditCardIcon, UserIcon, BuildingIcon, FileTextIcon } from 'lucide-react';
import { Sale } from '../../../shared/types/Transaction';
import { RefundModal } from '../../refunds/components/RefundModal';
import { TransactionFinancialBreakdown } from '../../../shared/ui/TransactionFinancialBreakdown';
import { SCENARIOS, buildScenario } from '../../payments/utils/transactionScenarios';
import { getMockSaleById, getSaleFixture } from '../../../shared/mocks/mockSales';
import { computeTransactionViewModel } from '../../payments/utils/transactionViewModel';
import { formatMoney } from '../../../shared/lib/money';
export function SaleDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  // Get sale fixture and initialize scenario from it
  const saleFixture = getSaleFixture(id || 'payment-with-reserve');
  const [selectedScenarioId, setSelectedScenarioId] = useState(saleFixture?.defaultScenarioId || 'payment-with-reserve');
  // Get base sale data from shared fixtures
  const baseSale = getMockSaleById(id || 'payment-with-reserve');
  // Compute current scenario data (updates when scenario changes)
  const currentScenario = useMemo(() => {
    return buildScenario(selectedScenarioId);
  }, [selectedScenarioId]);
  const currentViewModel = useMemo(() => {
    return computeTransactionViewModel(currentScenario.transaction, currentScenario.ledgerRows, 'included');
  }, [currentScenario]);
  // Build sale object with current scenario data
  const sale: Sale = useMemo(() => {
    if (!baseSale) {
      // Fallback if sale not found
      return {
        id: id || 'unknown',
        transactionId: currentScenario.transaction.transactionId,
        amount: currentViewModel.headerAmount,
        currency: currentScenario.transaction.currency,
        status: 'completed',
        source: 'payment_link',
        date: currentScenario.transaction.date,
        customerName: 'Unknown Customer',
        customerEmail: 'unknown@example.com',
        paymentMethod: {
          type: 'card'
        },
        merchantName: 'Unknown Merchant',
        merchantId: 'MERCH-000',
        description: currentScenario.description,
        fee: currentViewModel.summary.feesPostedDisplay,
        netAmount: currentViewModel.summary.netPosted,
        lifecycle: []
      };
    }
    // Override transaction data with current scenario
    return {
      ...baseSale,
      transactionId: currentScenario.transaction.transactionId,
      amount: currentViewModel.headerAmount,
      currency: currentScenario.transaction.currency,
      date: currentScenario.transaction.date,
      fee: currentViewModel.summary.feesPostedDisplay,
      netAmount: currentViewModel.summary.netPosted
    };
  }, [baseSale, currentScenario, currentViewModel, id]);
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      refund_initiated: 'bg-emerald-100 text-emerald-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-11630">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const handleRefund = (amount: number, reason: string, notes: string) => {
    console.log('Refund initiated:', {
      transactionId: sale.transactionId,
      amount,
      reason,
      notes
    });
    // In a real app, this would call an API to process the refund
    alert(`Refund of ${formatMoney(amount, sale.currency)} initiated successfully!`);
  };
  return <div className="space-y-6" data-id="element-11631">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-11632">
        <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-11633">
          <ArrowLeftIcon size={20} data-id="element-11634" />
          {t('pages.SaleDetail.back_to_sales')}
        </button>
        <div className="flex items-center gap-3" data-id="element-11635">
          {/* Dev-only scenario selector */}
          <select value={selectedScenarioId} onChange={e => setSelectedScenarioId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50" data-id="element-11636">
            {SCENARIOS.map(scenario => <option key={scenario.scenarioId} value={scenario.scenarioId} data-id="element-11637">
                {scenario.description}
              </option>)}
          </select>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-11638">
            <DownloadIcon size={18} data-id="element-11639" />
            {t('pages.SaleDetail.download_receipt')}
          </button>
          {sale.status === 'completed' && <button onClick={() => setRefundModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-11640">
              <RefreshCwIcon size={18} data-id="element-11641" />
              {t('pages.SaleDetail.refund')}
            </button>}
        </div>
      </div>
      {/* Transaction Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-11642">
        <div className="flex items-start justify-between mb-6" data-id="element-11643">
          <div data-id="element-11644">
            <div className="text-4xl font-bold text-gray-900" data-id="element-11645">
              {formatMoney(sale.amount, sale.currency)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mt-2" data-id="element-11646">
              {sale.transactionId}
            </h1>
          </div>
          <div data-id="element-11647">{getStatusBadge(sale.status)}</div>
        </div>

        {/* Transaction Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11648">
          {/* Customer Information */}
          <div className="space-y-4" data-id="element-11649">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-11650">
              <UserIcon size={20} className="text-blue-600" data-id="element-11651" />
              {t('pages.OrderDetail.customer_information')}
            </h3>
            <div className="space-y-3" data-id="element-11652">
              <div data-id="element-11653">
                <div className="text-sm text-gray-500" data-id="element-11654">
                  {t('pages.SaleDetail.name')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-11655">
                  {sale.customerName}
                </div>
              </div>
              <div data-id="element-11656">
                <div className="text-sm text-gray-500" data-id="element-11657">
                  {t('pages.OrderDetail.email')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-11658">
                  {sale.customerEmail}
                </div>
              </div>
              {sale.customerPhone && <div data-id="element-11659">
                  <div className="text-sm text-gray-500" data-id="element-11660">
                    {t('pages.SaleDetail.phone')}
                  </div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-11661">
                    {sale.customerPhone}
                  </div>
                </div>}
            </div>
          </div>
          {/* Payment Information */}
          <div className="space-y-4" data-id="element-11662">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-11663">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-11664" />
              {t('pages.OrderDetail.payment_information')}
            </h3>
            <div className="space-y-3" data-id="element-11665">
              <div data-id="element-11666">
                <div className="text-sm text-gray-500" data-id="element-11667">
                  {t('pages.OrderDetail.payment_method')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-11668">
                  {sale.paymentMethod.brand || sale.paymentMethod.type}{' '}
                  {sale.paymentMethod.last4 && `•••• ${sale.paymentMethod.last4}`}
                  {sale.paymentMethod.details && sale.paymentMethod.details}
                </div>
              </div>
              <div data-id="element-11669">
                <div className="text-sm text-gray-500" data-id="element-11670">
                  {t('pages.SaleDetail.transaction_date')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-11671">
                  {new Date(sale.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              <div data-id="element-11672">
                <div className="text-sm text-gray-500" data-id="element-11673">
                  {t('pages.SaleDetail.source')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-11674">
                  {sale.source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </div>
              </div>
            </div>
          </div>
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-11675">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-11676">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-11677" />
              {t('pages.SaleDetail.merchant_information')}
            </h3>
            <div className="space-y-3" data-id="element-11678">
              <div data-id="element-11679">
                <div className="text-sm text-gray-500" data-id="element-11680">
                  {t('pages.SaleDetail.merchant_name')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-11681">
                  {sale.merchantName}
                </div>
              </div>
              <div data-id="element-11682">
                <div className="text-sm text-gray-500" data-id="element-11683">
                  {t('pages.SaleDetail.merchant_id')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-11684">
                  {sale.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Additional Details */}
          {sale.invoiceId && <div className="space-y-4" data-id="element-11685">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-11686">
                <FileTextIcon size={20} className="text-blue-600" data-id="element-11687" />
                {t('pages.SaleDetail.related_documents')}
              </h3>
              <div className="space-y-3" data-id="element-11688">
                <div data-id="element-11689">
                  <div className="text-sm text-gray-500" data-id="element-11690">
                    {t('pages.SaleDetail.invoice')}
                  </div>
                  <button onClick={() => navigate(`/invoice-detail/${sale.invoiceId}`)} className="text-sm font-medium text-blue-600 hover:underline" data-id="element-11691">
                    {sale.invoiceId}
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* New Financial Breakdown */}
      <TransactionFinancialBreakdown transactionId={sale.transactionId} scenarioId={selectedScenarioId} currency={sale.currency} data-id="element-11692" />

      {/* Transaction Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-11693">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-11694">
          {t('pages.SaleDetail.transaction_lifecycle')}
        </h2>
        <div className="space-y-6" data-id="element-11695">
          {currentScenario.ledgerRows.filter(row => row.status === 'Posted').map((row, index, filteredRows) => <div key={row.id} className="relative" data-id="element-11696">
                {index !== filteredRows.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-11697"></div>}
                <div className="flex items-start gap-4" data-id="element-11698">
                  <div className="flex-shrink-0" data-id="element-11699">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-11700">
                      <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-11701"></div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-11702">
                    <div className="flex items-center justify-between" data-id="element-11703">
                      <div className="flex items-center gap-3" data-id="element-11704">
                        <p className="text-sm font-medium text-gray-900" data-id="element-11705">
                          {row.posting}
                        </p>
                        {getStatusBadge(row.status.toLowerCase())}
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-11706">{row.effectiveAt}</p>
                    </div>
                  </div>
                </div>
              </div>)}
        </div>
      </div>

      {/* Refund Modal */}
      <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={sale.amount} transactionId={sale.transactionId} currency="MVR" onRefund={handleRefund} data-id="element-11707" />
    </div>;
}